import api from './api';

// =====================================================================
// Turnier-Anmeldesystem (Entry-System) – Service
//
// WICHTIG: Das Backend ist uneinheitlich:
//  - Erfolg meist als direktes Objekt/Array (KEIN {success,data}-Wrapper)
//  - Fehler meist als plain String mit Status 400/403/404
// Deshalb: ALLE Felder nullable/optional + defensives Parsing.
// =====================================================================

// ---------------------------------------------------------------------
// Typen
// ---------------------------------------------------------------------

export type EntryStatus =
  | 'PENDING'
  | 'CONFIRMED'
  | 'PAID'
  | 'REJECTED'
  | 'WAITLIST'
  | 'CANCELLED';

export interface CustomEntryInput {
  id?: string | null;
  value?: string | null;
}

export interface FieldOption {
  id?: string | null;
  label?: string | null;
  value?: string | null;
}

export interface CustomEntryField {
  id?: string | null;
  name?: string | null;
  type?: string | null; // text | textarea | number | date | select | checkbox | radio | email | phone
  required?: boolean | null;
  perPlayer?: boolean | null;
  description?: string | null;
  placeholder?: string | null;
  min?: number | null;
  max?: number | null;
  maxLength?: number | null;
  order?: number | null;
  options?: FieldOption[] | null;
}

export interface Entry {
  id?: string | null;
  registerable?: string | null;
  owner?: string | null;
  firstname?: string | null;
  lastname?: string | null;
  email?: string | null;
  phone?: string | null;
  birthday?: number | null;
  address?: string | null;
  paid?: boolean | null;
  paidEntryFee?: boolean | null;
  waiting?: boolean | null;
  status?: EntryStatus | string | null;
  rejectionReason?: string | null;
  customEntryInputs?: CustomEntryInput[] | null;
  createdAt?: number | null;
  confirmedAt?: number | null;
  paidAt?: number | null;
  checkedIn?: boolean | null;
}

export interface TeamPlayer {
  firstname?: string | null;
  lastname?: string | null;
  email?: string | null;
  phone?: string | null;
  birthday?: number | null;
  userId?: string | null;
  username?: string | null;
  isSubstitute?: boolean | null;
  customEntryInputs?: CustomEntryInput[] | null;
}

export interface TeamEntry {
  id?: string | null;
  registerable?: string | null;
  owner?: string | null;
  teamName?: string | null;
  players?: TeamPlayer[] | null;
  paid?: boolean | null;
  paidEntryFee?: boolean | null;
  waiting?: boolean | null;
  status?: EntryStatus | string | null;
  rejectionReason?: string | null;
  customEntryInput?: string | null;
  customEntryInputs?: CustomEntryInput[] | null;
  invitationCode?: string | null;
  invitationEnabled?: boolean | null;
  maxPlayers?: number | null;
  createdAt?: number | null;
}

export interface PaymentInfo {
  bankName?: string | null;
  iban?: string | null;
  bic?: string | null;
  accountHolder?: string | null;
  paymentReference?: string | null;
  additionalInfo?: string | null;
  paypalLink?: string | null;
}

/** Turnier-Konfiguration der Anmeldeseite (GET /api/registration/:slug). */
export interface RegistrationConfig {
  id?: string | null;
  name?: string | null;
  slug?: string | null;
  image?: string | null;
  dates?: number[] | null;
  address?: any | null;
  email?: string | null;
  phone?: string | null;
  type?: string | null; // SINGLE | DOUBLE | TEAM
  entryEnabled?: boolean | null;
  entryDeadline?: number | null;
  entryDeadlineOnSite?: { hour?: number | null; minute?: number | null } | null;
  entryFee?: number | null;
  entryLimit?: number | null;
  entryText?: string | null;
  entryCode?: string | null;
  entryTime?: { hour?: number | null; minute?: number | null } | null;
  customEntryHint?: string | null;
  customEntryFields?: CustomEntryField[] | null;
  teamSize?: number | null;
  teamSizeMax?: number | null;
  entryBirthdayRequired?: boolean | null;
  entryEmailRequired?: boolean | null;
  entryPhoneRequired?: boolean | null;
  minAge?: number | null;
  maxAge?: number | null;
  entryChangeableUntil?: number | null;
  leaveEntryAllowed?: boolean | null;
  waitingEnabled?: boolean | null;
  entryPlaceholder?: string | null;
  registrationType?: string | null; // DARTCLOUD | EXTERNAL | NONE
  requireConfirmation?: boolean | null;
  paymentInfo?: PaymentInfo | null;
}

export interface RegistrationPageData {
  type?: 'DARTCLOUD' | 'EXTERNAL' | string | null;
  // Bei EXTERNAL:
  externalLink?: string | null;
  tournamentName?: string | null;
  tournamentSlug?: string | null;
  tournamentId?: string | null;
  // Bei DARTCLOUD:
  tournament?: RegistrationConfig | null;
  registrationUrl?: string | null;
}

export interface LightEntry {
  firstname?: string | null;
  lastname?: string | null;
}

export interface LightTeamEntry {
  teamName?: string | null;
  players?: LightEntry[] | null;
}

export interface DuplicateCheckResult {
  isDuplicate?: boolean | null;
  duplicateType?: string | null; // 'name' | 'email'
  message?: string | null;
}

export interface TeamDuplicateInfo {
  player?: string | null;
  duplicateType?: string | null;
  existingTeam?: string | null;
  message?: string | null;
}

export interface TeamDuplicateCheckResult {
  hasDuplicates?: boolean | null;
  duplicates?: TeamDuplicateInfo[] | null;
}

export interface TeamInvitationInfo {
  teamId?: string | null;
  teamName?: string | null;
  currentPlayers?: number | null;
  maxPlayers?: number | null;
  tournamentName?: string | null;
  tournamentSlug?: string | null;
  tournamentId?: string | null;
  invitationEnabled?: boolean | null;
  players?: { name?: string | null }[] | null;
}

export interface UserRegistrationRegisterable {
  id?: string | null;
  name?: string | null;
  slug?: string | null;
  date?: number | null;
  location?: string | null;
  city?: string | null;
  type?: string | null;
}

export interface UserRegistration {
  type?: 'single' | 'team' | string | null;
  entryId?: string | null;
  registerable?: UserRegistrationRegisterable | null;
  entry?: {
    firstname?: string | null;
    lastname?: string | null;
    email?: string | null;
    teamName?: string | null;
    players?: { firstname?: string | null; lastname?: string | null }[] | null;
    createdAt?: number | null;
    invitationCode?: string | null;
    invitationEnabled?: boolean | null;
  } | null;
  status?: EntryStatus | string | null;
  paid?: boolean | null;
  waiting?: boolean | null;
  isOwner?: boolean | null;
}

export interface UserRegistrationsResponse {
  upcoming?: UserRegistration[] | null;
  past?: UserRegistration[] | null;
  total?: number | null;
}

// Payloads -------------------------------------------------------------

export interface EnterTournamentPayload {
  firstname: string;
  lastname: string;
  email?: string | null;
  phone?: string | null;
  birthday?: number | null;
  address?: string | null;
  customEntryInputs?: CustomEntryInput[] | null;
}

export interface TeamPlayerPayload {
  firstname: string;
  lastname: string;
  email?: string | null;
  phone?: string | null;
  birthday?: number | null;
  username?: string | null;
  customEntryInputs?: CustomEntryInput[] | null;
}

export interface EnterTeamPayload {
  teamName?: string | null;
  players: TeamPlayerPayload[];
  customInput?: string | null;
  customEntryInputs?: CustomEntryInput[] | null;
}

// ---------------------------------------------------------------------
// Defensive Parser
// ---------------------------------------------------------------------

function safeArray<T>(data: any): T[] {
  if (!data || !Array.isArray(data)) return [];
  return data.filter((item) => item != null) as T[];
}

function safeObject<T>(data: any): T | null {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return null;
  return data as T;
}

/** Entfernt undefined/null-Felder aus einem Request-Body (Backend-Joi mag kein explizites undefined). */
function cleanBody(obj: Record<string, any>): Record<string, any> {
  const out: Record<string, any> = {};
  for (const key of Object.keys(obj)) {
    if (obj[key] !== undefined) out[key] = obj[key];
  }
  return out;
}

// ---------------------------------------------------------------------
// Service
// ---------------------------------------------------------------------

class EntryService {
  // ============== Anmeldeseite ==============

  /** GET /api/registration/:slug (public) – komplette Daten für die Anmeldeseite. */
  async getRegistrationData(slug: string): Promise<RegistrationPageData | null> {
    const response = await api.get(`/api/registration/${encodeURIComponent(slug)}`);
    return safeObject<RegistrationPageData>(response.data);
  }

  // ============== Anmelden / Abmelden ==============

  /** POST /api/registerable/:id/enter (auth) – Einzel-Anmeldung. Response: Entry-Objekt direkt. */
  async enterTournament(registerableId: string, payload: EnterTournamentPayload): Promise<Entry | null> {
    const body = cleanBody({
      firstname: payload.firstname,
      lastname: payload.lastname,
      email: payload.email ?? null,
      phone: payload.phone ?? null,
      birthday: payload.birthday ?? null,
      address: payload.address ?? undefined,
      customEntryInputs: payload.customEntryInputs ?? undefined,
    });
    const response = await api.post(`/api/registerable/${encodeURIComponent(registerableId)}/enter`, body);
    return safeObject<Entry>(response.data);
  }

  /** POST /api/registerable/:id/enter/team (auth) – Team-Anmeldung. Response: TeamEntry-Objekt direkt. */
  async enterTeam(registerableId: string, payload: EnterTeamPayload): Promise<TeamEntry | null> {
    const body = cleanBody({
      teamName: payload.teamName ?? null,
      customInput: payload.customInput ?? null,
      customEntryInputs: payload.customEntryInputs ?? [],
      players: (payload.players || []).map((p) =>
        cleanBody({
          firstname: p.firstname,
          lastname: p.lastname,
          email: p.email ?? null,
          phone: p.phone ?? null,
          birthday: p.birthday ?? null,
          username: p.username ?? null,
          customEntryInputs: p.customEntryInputs ?? undefined,
        })
      ),
      // Pflicht-Versionsflags des Backends (siehe enterRegisterableTeam Controller)
      newVersion: true,
      versionNumber: 7,
    });
    const response = await api.post(`/api/registerable/${encodeURIComponent(registerableId)}/enter/team`, body);
    return safeObject<TeamEntry>(response.data);
  }

  /** POST /api/registerable/:id/leave (auth) – Einzel-Abmeldung (auch Team-Mitspieler via altem Flow). */
  async leaveRegisterable(registerableId: string): Promise<void> {
    await api.post(`/api/registerable/${encodeURIComponent(registerableId)}/leave`, {});
  }

  /** POST /api/registerable/:id/leave/team (auth) – Team-Abmeldung (gesamtes Team, nur Owner). */
  async leaveRegisterableTeam(registerableId: string): Promise<void> {
    await api.post(`/api/registerable/${encodeURIComponent(registerableId)}/leave/team`, {});
  }

  /** POST /api/entry/team/:id/leave (auth) – Team als Mitspieler verlassen (nicht Owner). */
  async leaveTeamAsPlayer(teamEntryId: string): Promise<void> {
    await api.post(`/api/entry/team/${encodeURIComponent(teamEntryId)}/leave`, {});
  }

  // ============== Eigene Anmeldungen ==============

  /** GET /api/user/entries (auth) – Einzel-Anmeldungen des Users (direktes Array). */
  async getUserEntries(): Promise<Entry[]> {
    const response = await api.get('/api/user/entries');
    return safeArray<Entry>(response.data);
  }

  /** GET /api/user/team/entries/owner (auth) – Team-Anmeldungen als Owner (direktes Array). */
  async getTeamEntriesAsOwner(): Promise<TeamEntry[]> {
    const response = await api.get('/api/user/team/entries/owner');
    return safeArray<TeamEntry>(response.data);
  }

  /** GET /api/user/team/entries/player (auth) – Team-Anmeldungen als Mitspieler (direktes Array). */
  async getTeamEntriesAsPlayer(): Promise<TeamEntry[]> {
    const response = await api.get('/api/user/team/entries/player');
    return safeArray<TeamEntry>(response.data);
  }

  /** GET /api/user/registrations (auth) – Übersicht aller Anmeldungen mit Turnier-Infos. */
  async getUserRegistrations(): Promise<UserRegistrationsResponse> {
    const response = await api.get('/api/user/registrations');
    const data = safeObject<UserRegistrationsResponse>(response.data);
    return {
      upcoming: safeArray<UserRegistration>(data?.upcoming),
      past: safeArray<UserRegistration>(data?.past),
      total: typeof data?.total === 'number' ? data.total : null,
    };
  }

  // ============== Anmeldung bearbeiten / bestätigen ==============

  /** POST /api/entry/:id/update (auth) – Einzel-Anmeldung aktualisieren. Response: Entry. */
  async updateEntry(
    entryId: string,
    payload: Partial<EnterTournamentPayload>
  ): Promise<Entry | null> {
    const body = cleanBody({
      firstname: payload.firstname,
      lastname: payload.lastname,
      email: payload.email ?? undefined,
      phone: payload.phone ?? undefined,
      birthday: payload.birthday ?? undefined,
      customEntryInputs: payload.customEntryInputs ?? undefined,
    });
    const response = await api.post(`/api/entry/${encodeURIComponent(entryId)}/update`, body);
    return safeObject<Entry>(response.data);
  }

  /** POST /api/entry/:id/team/update (auth) – Team-Anmeldung aktualisieren. Response: TeamEntry. */
  async updateTeamEntry(
    entryId: string,
    payload: Partial<EnterTeamPayload>
  ): Promise<TeamEntry | null> {
    const body = cleanBody({
      teamName: payload.teamName ?? null,
      customInput: payload.customInput ?? undefined,
      customEntryInputs: payload.customEntryInputs ?? [],
      players: payload.players
        ? payload.players.map((p) =>
            cleanBody({
              firstname: p.firstname,
              lastname: p.lastname,
              email: p.email ?? undefined,
              phone: p.phone ?? undefined,
              birthday: p.birthday ?? undefined,
              username: p.username ?? undefined,
              customEntryInputs: p.customEntryInputs ?? undefined,
            })
          )
        : undefined,
    });
    const response = await api.post(`/api/entry/${encodeURIComponent(entryId)}/team/update`, body);
    return safeObject<TeamEntry>(response.data);
  }

  /**
   * POST /api/entry/:id/confirm (auth) – Anmeldung bestätigen (paid).
   * HINWEIS: Laut Backend-Controller nur für den TURNIER-Veranstalter erlaubt.
   */
  async confirmEntry(entryId: string, paid: boolean): Promise<void> {
    await api.post(`/api/entry/${encodeURIComponent(entryId)}/confirm`, { paid });
  }

  /** POST /api/entry/:id/confirm/team (auth) – Team-Anmeldung bestätigen (nur Veranstalter/Admin). */
  async confirmTeamEntry(entryId: string, paid: boolean): Promise<void> {
    await api.post(`/api/entry/${encodeURIComponent(entryId)}/confirm/team`, { paid });
  }

  // ============== Duplicate-Check ==============

  /** POST /api/registerable/:id/entries/check-duplicate (public). */
  async checkDuplicate(
    registerableId: string,
    firstname: string,
    lastname: string,
    email?: string | null
  ): Promise<DuplicateCheckResult> {
    const response = await api.post(
      `/api/registerable/${encodeURIComponent(registerableId)}/entries/check-duplicate`,
      cleanBody({ firstname, lastname, email: email || null })
    );
    return safeObject<DuplicateCheckResult>(response.data) || {};
  }

  /** POST /api/registerable/:id/entries/team/check-duplicate (public). */
  async checkTeamDuplicate(
    registerableId: string,
    players: { firstname: string; lastname: string; email?: string | null }[],
    excludeTeamId?: string | null
  ): Promise<TeamDuplicateCheckResult> {
    const response = await api.post(
      `/api/registerable/${encodeURIComponent(registerableId)}/entries/team/check-duplicate`,
      cleanBody({
        players: players.map((p) => cleanBody({ firstname: p.firstname, lastname: p.lastname, email: p.email || null })),
        excludeTeamId: excludeTeamId ?? undefined,
      })
    );
    return safeObject<TeamDuplicateCheckResult>(response.data) || {};
  }

  // ============== Team-Einladungen ==============

  /** GET /api/entry/team/invitation/:code (public). */
  async getTeamByInvitationCode(code: string): Promise<TeamInvitationInfo | null> {
    const response = await api.get(`/api/entry/team/invitation/${encodeURIComponent(code)}`);
    return safeObject<TeamInvitationInfo>(response.data);
  }

  /** POST /api/entry/team/invitation/:code/join (auth). */
  async joinTeamByInvitation(
    code: string,
    payload: { firstname: string; lastname: string; email?: string | null; phone?: string | null; birthday?: number | null }
  ): Promise<{ success?: boolean | null; message?: string | null; team?: any } | null> {
    const response = await api.post(
      `/api/entry/team/invitation/${encodeURIComponent(code)}/join`,
      cleanBody({
        firstname: payload.firstname,
        lastname: payload.lastname,
        email: payload.email || null,
        phone: payload.phone || null,
        birthday: payload.birthday ?? null,
      })
    );
    return safeObject(response.data);
  }

  /** POST /api/entry/team/:id/invitation/regenerate (auth, nur Team-Owner). */
  async regenerateInvitationCode(
    teamEntryId: string
  ): Promise<{ success?: boolean | null; invitationCode?: string | null; invitationLink?: string | null } | null> {
    const response = await api.post(`/api/entry/team/${encodeURIComponent(teamEntryId)}/invitation/regenerate`, {});
    return safeObject(response.data);
  }

  /** POST /api/entry/team/:id/invitation/toggle (auth, nur Team-Owner). */
  async toggleTeamInvitation(
    teamEntryId: string
  ): Promise<{ success?: boolean | null; invitationEnabled?: boolean | null; message?: string | null } | null> {
    const response = await api.post(`/api/entry/team/${encodeURIComponent(teamEntryId)}/invitation/toggle`, {});
    return safeObject(response.data);
  }

  // ============== Teilnehmerlisten (light) ==============

  /** GET /api/registerable/:id/entries/light (auth) – nur sichtbar wenn showEntryList aktiv. */
  async getLightEntries(registerableId: string): Promise<LightEntry[]> {
    const response = await api.get(`/api/registerable/${encodeURIComponent(registerableId)}/entries/light`);
    return safeArray<LightEntry>(response.data);
  }

  /** GET /api/registerable/:id/waiting/light (auth) – nur sichtbar wenn showWaitingList aktiv. */
  async getLightWaiting(registerableId: string): Promise<LightEntry[]> {
    const response = await api.get(`/api/registerable/${encodeURIComponent(registerableId)}/waiting/light`);
    return safeArray<LightEntry>(response.data);
  }

  /** GET /api/registerable/:id/entries/team/light (auth). */
  async getLightTeamEntries(registerableId: string): Promise<LightTeamEntry[]> {
    const response = await api.get(`/api/registerable/${encodeURIComponent(registerableId)}/entries/team/light`);
    return safeArray<LightTeamEntry>(response.data);
  }

  /** GET /api/registerable/:id/waiting/team/light (auth). */
  async getLightTeamWaiting(registerableId: string): Promise<LightTeamEntry[]> {
    const response = await api.get(`/api/registerable/${encodeURIComponent(registerableId)}/waiting/team/light`);
    return safeArray<LightTeamEntry>(response.data);
  }

  // ===================================================================
  // Helper
  // ===================================================================

  /** Effektiver Status (wie Flutter effectiveStatus): status > paid > waiting > PENDING. */
  getEffectiveStatus(entry?: { status?: string | null; paid?: boolean | null; waiting?: boolean | null } | null): EntryStatus {
    if (!entry) return 'PENDING';
    const s = (entry.status || '').toString().toUpperCase();
    if (s === 'PENDING' || s === 'CONFIRMED' || s === 'PAID' || s === 'REJECTED' || s === 'WAITLIST' || s === 'CANCELLED') {
      return s as EntryStatus;
    }
    if (entry.paid === true) return 'PAID';
    if (entry.waiting === true) return 'WAITLIST';
    return 'PENDING';
  }

  /** Deutsches Status-Label. */
  getStatusLabel(status?: string | null): string {
    switch ((status || '').toString().toUpperCase()) {
      case 'PAID':
        return 'Bezahlt';
      case 'CONFIRMED':
        return 'Bestätigt';
      case 'REJECTED':
        return 'Abgelehnt';
      case 'WAITLIST':
        return 'Warteliste';
      case 'CANCELLED':
        return 'Storniert';
      case 'PENDING':
        return 'Offen';
      default:
        return 'Offen';
    }
  }

  /** Status-Farbe (Hex). */
  getStatusColor(status?: string | null): string {
    switch ((status || '').toString().toUpperCase()) {
      case 'PAID':
        return '#10B981'; // grün
      case 'CONFIRMED':
        return '#3FCED1'; // akzent
      case 'REJECTED':
        return '#EF4444'; // rot
      case 'CANCELLED':
        return '#EF4444';
      case 'WAITLIST':
        return '#F59E0B'; // orange
      case 'PENDING':
      default:
        return '#9CA3AF'; // grau
    }
  }

  /** Datum (Timestamp) als deutsches Datum formatieren. */
  formatDate(timestamp?: number | null): string {
    if (!timestamp || typeof timestamp !== 'number' || isNaN(timestamp)) return 'Datum unbekannt';
    try {
      const d = new Date(timestamp);
      if (isNaN(d.getTime())) return 'Datum unbekannt';
      return d.toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch {
      return 'Datum unbekannt';
    }
  }

  /** Datum (Timestamp) kurz: TT.MM.JJJJ. */
  formatShortDate(timestamp?: number | null): string {
    if (!timestamp || typeof timestamp !== 'number' || isNaN(timestamp)) return '';
    const d = new Date(timestamp);
    if (isNaN(d.getTime())) return '';
    return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.${d.getFullYear()}`;
  }

  /** Geburtstag (Timestamp) ⇄ Input-Value (YYYY-MM-DD). */
  birthdayToInputValue(timestamp?: number | null): string {
    if (!timestamp || typeof timestamp !== 'number' || isNaN(timestamp)) return '';
    const d = new Date(timestamp);
    if (isNaN(d.getTime())) return '';
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }

  inputValueToBirthday(value?: string | null): number | null {
    if (!value || typeof value !== 'string') return null;
    const d = new Date(`${value}T00:00:00`);
    if (isNaN(d.getTime())) return null;
    return d.getTime();
  }

  /** Fehlertext aus Axios-Fehler extrahieren (Backend sendet plain Strings mit Status 400). */
  getErrorMessage(error: any, fallback = 'Es ist ein Fehler aufgetreten.'): string {
    const data = error?.response?.data;
    if (typeof data === 'string' && data.trim().length > 0) return data;
    if (data && typeof data === 'object') {
      if (typeof data.message === 'string' && data.message.trim().length > 0) return data.message;
      if (typeof data.error === 'string' && data.error.trim().length > 0) return data.error;
    }
    if (typeof error?.message === 'string' && error.message.includes('Network')) {
      return 'Netzwerkfehler – bitte Verbindung prüfen.';
    }
    return fallback;
  }

  /** Validierung eines Custom-Fields (Port der Flutter-Logik CustomEntryField.validate). */
  validateCustomField(field?: CustomEntryField | null, value?: any): string | null {
    if (!field) return null;
    const name = field.name || 'Feld';
    const empty = value === null || value === undefined || value.toString().trim() === '';

    if (field.required === true && empty) {
      return `${name} ist erforderlich`;
    }
    if (empty) return null;

    const type = (field.type || 'text').toLowerCase();
    const str = value.toString();

    if (type === 'number') {
      const num = Number(str.replace(',', '.'));
      if (isNaN(num)) return `${name} muss eine Zahl sein`;
      if (field.min != null && num < field.min) return `${name} muss mindestens ${field.min} sein`;
      if (field.max != null && num > field.max) return `${name} darf maximal ${field.max} sein`;
    } else if (type === 'text' || type === 'textarea') {
      if (field.maxLength != null && str.length > field.maxLength) {
        return `${name} darf maximal ${field.maxLength} Zeichen haben`;
      }
    } else if (type === 'email') {
      const emailRegex = /^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/;
      if (!emailRegex.test(str)) return 'Ungültige E-Mail-Adresse';
    } else if (type === 'phone') {
      const phoneRegex = /^[+]?[(]?[0-9]{1,4}[)]?[-\s./0-9]*$/;
      if (!phoneRegex.test(str)) return 'Ungültige Telefonnummer';
    }

    return null;
  }

  /** Sortierte Custom-Fields (nach order). */
  sortCustomFields(fields?: CustomEntryField[] | null): CustomEntryField[] {
    if (!fields || !Array.isArray(fields)) return [];
    return fields
      .filter((f) => f && typeof f === 'object')
      .slice()
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }

  /** Einladungslink für ein Team bauen. */
  buildInvitationLink(code?: string | null): string | null {
    if (!code) return null;
    const origin = typeof window !== 'undefined' ? window.location.origin : 'https://dartcloudapp.de';
    return `${origin}/team-einladung/${code}`;
  }
}

const entryService = new EntryService();
export default entryService;
